import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Event, FileAsset, ImageAsset } from '@rive-app/react-canvas';
import { Fit, Layout } from '@rive-app/react-canvas';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { loadRiveImage } from '../../../features/rewards/daily/dailyBonusV2/dailyBonusRiveHelpers';
import { getStampCardBodyCopy } from '../../../features/widgets/components/StampCardWidget/getStampCardBodyCopy';
import { useRiveMachine } from '../../../hooks/rive/useRiveMachine';
import { useCoinsAnimation } from '../../../hooks/useCoinsAnimation';
import { useLocale } from '../../../hooks/useLocale';
import { analytics } from '../../../services/analytics';
import { isStampLockedByTime, type StampCardStamp } from '../../../stores/stamp-card/types';
import type { ClaimableReward } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { devError, devLog } from '../../../utils/log';
import { formatTime } from '../../../utils/time';

import styles from './StampCardDialog.module.scss';
import { toPrizeLines } from './stampCardPrizes';

const MAX_STAMPS = 15;

const MAX_PRIZES = 5;

const JUST_STAMPED_CASCADE_MS = 700;

const JUST_STAMPED_LEAD_IN_MS = 900;

const SETTLE_FALLBACK_MS = 2500;

const CLAIM_FALLBACK_MS = 4000;

type StampState = 'LOCKED' | 'EMPTY' | 'STAMPED' | 'FREE_STAMP';

function getStampEnumValue(stamp: StampCardStamp | undefined): StampState {
  if (!stamp) return 'EMPTY';
  if (isStampLockedByTime(stamp)) return 'LOCKED';
  if (stamp.isFree) return 'FREE_STAMP';
  if (stamp.completedAt) return 'STAMPED';
  if (stamp.status === 'LOCKED' || stamp.status === 'AVAILABLE') return 'EMPTY';
  return 'STAMPED';
}

const stampPath = (stampIndex: number) => `stampsLayout/stamp${stampIndex}`;

function StampCardDialogComponent() {
  const { portalStore, stampCardStore } = useStores();
  const { t, i18n } = useTranslation();
  const { localeCurrency } = useLocale();
  const { startAnimation } = useCoinsAnimation();
  const { currentStampCard, justStampedIndexes } = stampCardStore;

  // Let the store's own `beforeClose` run (see `openStampCardDialog`): it resets
  // `isStampCardDialogOpen` *and* calls `onDialogFinished`. Suppressing it left that flag stuck
  // true, which gates both paths that re-queue this dialog — the socket handler and `fetch` — so
  // the pop-up would never auto-open again for the rest of the session.
  const close = useCallback(() => {
    portalStore.closePortal();
  }, [portalStore]);

  const onStateChange = useCallback((event: Event) => {
    const stateNames = (Array.isArray(event?.data) ? event.data : []).filter(
      (name): name is string => typeof name === 'string',
    );
    devLog('Stamp card Rive state change', stateNames);
  }, []);

  // Fires once per open, whichever surface opened it. Progress rides the type/value pair rather
  // than a property column, per the spec.
  const hasSentImpression = useRef(false);
  useEffect(() => {
    if (!currentStampCard || hasSentImpression.current) return;
    hasSentImpression.current = true;

    const stampsCollected = currentStampCard.stamps.filter((stamp) => stamp.status === 'COMPLETED').length;
    void analytics.sendStampCardEvent('popup', {
      event_parent: 'features',
      event_category: 'stamp_card',
      event_action: 'impression',
      event_type: 'stamps_collected',
      event_value: String(stampsCollected),
    });
  }, [currentStampCard]);

  const grandPrizeRewardPackage = currentStampCard?.stamps.find(
    (stamp) => stamp.stampIndex === currentStampCard.stampCount,
  )?.rewardPackage;

  const referencedAssets = useMemo(() => {
    const assets: Record<string, string> = {};
    toPrizeLines(grandPrizeRewardPackage, t)
      .slice(0, MAX_PRIZES)
      .forEach((item, index) => {
        if (item.imageUrl) assets[`placeholder_${index}`] = item.imageUrl;
      });
    return assets;
  }, [grandPrizeRewardPackage, t]);

  const assetLoader = useCallback(
    (asset: FileAsset, _bytes: Uint8Array) => {
      const url = asset.isImage && referencedAssets[asset.name];
      if (!url) return false;
      void loadRiveImage(url, asset as ImageAsset);
      return true;
    },
    [referencedAssets],
  );

  const {
    rive,
    RiveComponent,
    fireRiveViewModelTrigger,
    onRiveViewModelTrigger,
    setRiveViewModelEnum,
    setRiveViewModelBoolean,
  } = useRiveMachine({
    src: currentStampCard?.assets.popupLayoutUrl ?? undefined,
    artboard: 'Main',
    autoBind: true,
    stateMachineName: 'State Machine 1',
    assetLoader,
    onStateChange,
    onLoadError: (error) => {
      devError('Stamp card Rive load error', error);
      close();
    },
    layout: new Layout({
      fit: Fit.Contain,
    }),
  });

  const isClaimingRef = useRef(false);
  const claimedRewardsRef = useRef<ClaimableReward[] | null>(null);
  const [hasClaimed, setHasClaimed] = useState(false);
  const previousValuesRef = useRef<Record<number, StampState>>({});
  const activatedCardRef = useRef<string | null>(null);
  const justStampedRef = useRef<Set<number>>(new Set(justStampedIndexes));
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const pendingSettleRef = useRef<Record<number, StampState>>({});
  const awaitingClaimStampsRef = useRef(false);
  const claimFallbackRef = useRef<ReturnType<typeof setTimeout>>();

  const [canClaim, setCanClaim] = useState(false);

  const startClaimFlourish = useCallback(() => {
    setRiveViewModelBoolean('claimStamps', true);

    clearTimeout(claimFallbackRef.current);
    claimFallbackRef.current = setTimeout(() => {
      devError('Stamp card `claimedEnd` never fired; enabling claim anyway');
      setCanClaim(true);
    }, CLAIM_FALLBACK_MS);
  }, [setRiveViewModelBoolean]);

  useEffect(
    () => () => {
      timeoutsRef.current.forEach(clearTimeout);
      clearTimeout(claimFallbackRef.current);
    },
    [],
  );

  const settleStamp = useCallback(
    (stampIndex: number, viaFallback = false) => {
      const settled = pendingSettleRef.current[stampIndex];
      if (!settled) return;
      delete pendingSettleRef.current[stampIndex];

      if (viaFallback) devError('Stamp card `changeStamp` never fired; settling the stamp anyway', { stampIndex });
      setRiveViewModelEnum(`${stampPath(stampIndex)}/stampState`, settled);

      if (awaitingClaimStampsRef.current && Object.keys(pendingSettleRef.current).length === 0) {
        awaitingClaimStampsRef.current = false;
        startClaimFlourish();
      }
    },
    [setRiveViewModelEnum, startClaimFlourish],
  );

  const settleStampRef = useRef(settleStamp);
  settleStampRef.current = settleStamp;

  useEffect(() => {
    if (!rive) return undefined;

    const unsubscribes: (() => void)[] = [];
    for (let stampIndex = 1; stampIndex <= MAX_STAMPS; stampIndex++) {
      unsubscribes.push(
        onRiveViewModelTrigger(`${stampPath(stampIndex)}/changeStamp`, () => settleStampRef.current(stampIndex)),
      );
    }

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [rive, onRiveViewModelTrigger]);

  useEffect(() => {
    if (!rive || !currentStampCard) return;

    const vm = rive.viewModelInstance;
    if (!vm) return;

    const numOfStamps = vm.number?.('numOfStamps');
    if (numOfStamps) numOfStamps.value = currentStampCard.stampCount;

    const grandPrizeStamp = currentStampCard.stamps.find((stamp) => stamp.stampIndex === currentStampCard.stampCount);
    const rewardItems = toPrizeLines(grandPrizeStamp?.rewardPackage, t).slice(0, MAX_PRIZES);

    const numOfPrizes = vm.number?.('numOfPrizes');
    if (numOfPrizes) numOfPrizes.value = rewardItems.length;

    for (let prizeIndex = 1; prizeIndex <= MAX_PRIZES; prizeIndex++) {
      const item = rewardItems[prizeIndex - 1];
      if (!item) continue;

      setRiveViewModelEnum(`prizes/prize${prizeIndex}/prizeType`, item.prizeType);

      const coinsAmount = vm.string?.(`prizes/prize${prizeIndex}/coinsAmount`);
      if (coinsAmount) coinsAmount.value = item.coinsAmount;

      const placeholderNum = vm.number?.(`prizes/prize${prizeIndex}/placeholderNum`);
      if (placeholderNum && item.rawAmount != null) placeholderNum.value = item.rawAmount;
    }

    const isFirstActivation = activatedCardRef.current !== currentStampCard.userStampCardId;
    const animating: { stampIndex: number; settled: StampState }[] = [];

    for (let stampIndex = 1; stampIndex <= MAX_STAMPS; stampIndex++) {
      if (pendingSettleRef.current[stampIndex]) continue;

      const stamp = currentStampCard.stamps.find((s) => s.stampIndex === stampIndex);
      const settled = getStampEnumValue(stamp);
      const previous = previousValuesRef.current[stampIndex];
      previousValuesRef.current[stampIndex] = settled;

      const isNewGrant = !isFirstActivation && previous !== undefined && previous !== settled;
      const isJustStamped = justStampedRef.current.has(stampIndex);
      const isFreeReveal = !!stamp?.freeStampAnimationShown;

      if (isJustStamped || isNewGrant || isFreeReveal) {
        animating.push({ stampIndex, settled });
      } else {
        setRiveViewModelEnum(`${stampPath(stampIndex)}/stampState`, settled);
      }
    }

    animating.forEach(({ stampIndex, settled }, order) => {
      setRiveViewModelEnum(`${stampPath(stampIndex)}/stampState`, 'EMPTY');
      pendingSettleRef.current[stampIndex] = settled;

      const delayMs = JUST_STAMPED_LEAD_IN_MS + order * JUST_STAMPED_CASCADE_MS;
      timeoutsRef.current.push(
        setTimeout(() => {
          fireRiveViewModelTrigger(`${stampPath(stampIndex)}/stamped`);
          timeoutsRef.current.push(setTimeout(() => settleStampRef.current(stampIndex, true), SETTLE_FALLBACK_MS));
        }, delayMs),
      );
    });

    if (justStampedRef.current.size) {
      justStampedRef.current.clear();
      stampCardStore.consumeJustStamped();
    }
    if (
      animating.some(
        ({ stampIndex }) => currentStampCard.stamps.find((s) => s.stampIndex === stampIndex)?.freeStampAnimationShown,
      )
    ) {
      stampCardStore.consumeFreeStampReveals();
    }

    if (currentStampCard.completedAt) {
      if (Object.keys(pendingSettleRef.current).length > 0) {
        awaitingClaimStampsRef.current = true;
      } else {
        startClaimFlourish();
      }
    } else {
      awaitingClaimStampsRef.current = false;
      setRiveViewModelBoolean('claimStamps', false);
    }

    if (isFirstActivation && fireRiveViewModelTrigger('activate')) {
      activatedCardRef.current = currentStampCard.userStampCardId;
    }
  }, [
    rive,
    currentStampCard,
    setRiveViewModelEnum,
    setRiveViewModelBoolean,
    startClaimFlourish,
    fireRiveViewModelTrigger,
    stampCardStore,
    t,
  ]);

  useEffect(() => {
    clearTimeout(claimFallbackRef.current);
    setCanClaim(false);
  }, [currentStampCard?.userStampCardId]);

  useEffect(() => onRiveViewModelTrigger('claimedEnd', () => setCanClaim(true)), [onRiveViewModelTrigger]);

  useEffect(() => {
    const vm = rive?.viewModelInstance;

    const isWaitingOnFlourish = !!currentStampCard?.completedAt && !canClaim;
    setRiveViewModelBoolean('claimVisible', !hasClaimed && !isWaitingOnFlourish);

    const claimText = vm?.string?.('claimBTN/text');
    if (!claimText) return;

    claimText.value = canClaim ? t('common.buttons.claim') : t('common.buttons.awesome');
  }, [rive, currentStampCard?.completedAt, canClaim, hasClaimed, setRiveViewModelBoolean, t]);

  useEffect(() => {
    const bodyText = rive?.viewModelInstance?.string?.('bodyText');
    if (!bodyText || !currentStampCard) return;

    bodyText.value = getStampCardBodyCopy(currentStampCard, i18n.language, t, localeCurrency);
  }, [rive, currentStampCard, i18n.language, t, localeCurrency]);

  useEffect(() => {
    setRiveViewModelBoolean('isCanClaim', canClaim);
    setRiveViewModelBoolean('xVisible', !currentStampCard?.completedAt);
  }, [rive, currentStampCard?.completedAt, canClaim, setRiveViewModelBoolean]);

  useEffect(
    () =>
      onRiveViewModelTrigger('finish', () => {
        close();

        const rewards = claimedRewardsRef.current;
        claimedRewardsRef.current = null;
        if (rewards?.length) startAnimation({ rewards });
      }),
    [onRiveViewModelTrigger, close, startAnimation],
  );

  useEffect(() => {
    const userStampCardId = currentStampCard?.userStampCardId;
    if (!userStampCardId) return undefined;

    return onRiveViewModelTrigger('claimBTN/click', () => {
      if (canClaim) {
        isClaimingRef.current = true;
        setHasClaimed(true);
        const rewardPackage = stampCardStore.claimableStamp?.rewardPackage;
        claimedRewardsRef.current = rewardPackage
          ? [
              {
                scCoin: Number(rewardPackage.scCoin) || 0,
                gcCoin: Number(rewardPackage.gcCoin) || 0,
                bundleItems: rewardPackage.bundleItems ?? [],
              },
            ]
          : null;

        void stampCardStore.claimReward(userStampCardId).catch((error) => {
          isClaimingRef.current = false;
          setHasClaimed(false);
          claimedRewardsRef.current = null;
          devError('Stamp card claim failed', error);
        });
      } else {
        void analytics.sendStampCardEvent('popup_close', {
          event_parent: 'features',
          event_category: 'stamp_card',
          event_action: 'click',
          button_text: 'awesome',
        });
      }
    });
  }, [currentStampCard?.userStampCardId, canClaim, onRiveViewModelTrigger, stampCardStore, close]);

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    const timerProp = vm?.string?.('timer');
    const expiresAt = currentStampCard?.expiresAt;
    if (!timerProp || !expiresAt) return;

    const updateCountdown = () => {
      const secondsLeft = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      timerProp.value = formatTime(secondsLeft, 's', t);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [rive, currentStampCard?.expiresAt, t]);

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    if (!vm || !currentStampCard) return undefined;

    const updateLockTimers = () => {
      for (let stampIndex = 1; stampIndex <= MAX_STAMPS; stampIndex++) {
        const stamp = currentStampCard.stamps.find((s) => s.stampIndex === stampIndex);
        if (!stamp?.unlocksAt) continue;

        const lockTimerProp = vm.string?.(`stampsLayout/stamp${stampIndex}/lockTimer`);
        if (!lockTimerProp) continue;

        const secondsLeft = Math.max(0, Math.floor((new Date(stamp.unlocksAt).getTime() - Date.now()) / 1000));
        lockTimerProp.value = formatTime(secondsLeft, 's', t);

        if (secondsLeft === 0) {
          const unlockedStampAnimProp = vm.boolean?.(`stampsLayout/stamp${stampIndex}/unlockedStampAnim`);
          if (unlockedStampAnimProp) unlockedStampAnimProp.value = true;
        }
      }
    };

    updateLockTimers();
    const interval = setInterval(updateLockTimers, 1000);
    return () => clearInterval(interval);
  }, [rive, currentStampCard, t]);

  useEffect(() => {
    if (!currentStampCard && !isClaimingRef.current) close();
  }, [currentStampCard, close]);

  return (
    <div className={styles.container}>
      <RiveComponent className={styles.rive} />
    </div>
  );
}

export const StampCardDialog = observer(StampCardDialogComponent);
